import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import MobileHamburger from './MobileHamburger';
import MobileMenu from './MobileMenu';
import './Main.css';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from './supabaseClient';
import Preloader from './Preloader';
import { FaArrowLeft } from 'react-icons/fa6';

function TempShelterMonitoring() {
  console.log('TempShelterMonitoring component rendered');
  const navigate = useNavigate();
  const { id: shelterId } = useParams();
  const [shelter, setShelter] = useState(null);
  const [requesterHousehold, setRequesterHousehold] = useState(null);
  const [responderHousehold, setResponderHousehold] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editHousehold, setEditHousehold] = useState(null); // household being edited
  const [editMembers, setEditMembers] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [shelterHistory, setShelterHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyFilter, setHistoryFilter] = useState('all');
  const [currentUser, setCurrentUser] = useState(null);
  const [sidebarNarrow, setSidebarNarrow] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  useEffect(() => {
      document.title = "Approved Temporary Shelter Monitoring";
    }, []);

  useEffect(() => {
    console.log('useEffect running, shelterId:', shelterId);
    if (shelterId) {
    fetchShelterAndHouseholds();
    fetchShelterHistory();
    }
    // eslint-disable-next-line
  }, [shelterId]);

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  // Add audit log for viewing the shelter when currentUser and shelterId are available
  useEffect(() => {
    if (currentUser && shelterId) {
      (async () => {
        await supabase.from('audit_logs').insert([{
          user_id: currentUser.id,
          action: 'view temporary shelter',
          target_id: shelterId,
          target_type: 'TemporaryMonitoring',
          details: {}
        }]);
      })();
    }
  }, [currentUser, shelterId]);

  async function fetchShelterAndHouseholds() {
    setLoading(true);
    setError('');
    try {
      const { data: shelterData, error: shelterError } = await supabase
        .from('temp_shelter')
        .select('*')
        .eq('id', shelterId)
        .single();
        
      if (shelterError) throw shelterError;
      if (!shelterData) throw new Error("Shelter not found.");

      setShelter(shelterData);
      console.log('Shelter data fetched:', shelterData);

      // --- New household fetching logic ---
      let requesterUserId = null;
      let responderUserId = null;

      // 1. Get responder's user_id from their profile via shelter_provider
      if (shelterData.shelter_provider) {
        console.log(`Fetching responder profile with shelter_provider ID: ${shelterData.shelter_provider}`);
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('user_id')
          .eq('id', shelterData.shelter_provider)
          .single();

        if (profileError) {
          console.error('Error fetching responder profile:', profileError);
        } else if (profile && profile.user_id) {
          responderUserId = profile.user_id;
          console.log(`Found responder user_id: ${responderUserId}`);
        }
      } else {
        console.log('No shelter_provider found on shelter data.');
      }

      // 2. Find the requester's user_id via the sos_beacons table using the responder's ID
      if (responderUserId) {
        console.log(`Using responder's user_id (${responderUserId}) to find the associated requester in sos_beacons.`);
        const { data: beacon, error: beaconError } = await supabase
          .from('sos_beacons')
          .select('user_id')
          .eq('responder_id', responderUserId)
          .limit(1) // Assuming the first match is the relevant one
          .single();

        if (beaconError) {
          console.error(`Error fetching beacon with responder_id ${responderUserId}:`, beaconError);
        } else if (beacon) {
          requesterUserId = beacon.user_id;
          console.log(`Found requester user_id from beacon: ${requesterUserId}`);
        } else {
          console.log(`No beacon found for responder_id ${responderUserId}.`);
        }
      }

      // 3. Fetch households based on the IDs we found
      if (requesterUserId && requesterUserId === responderUserId) {
        console.log(`Requester and Responder are the same user (${requesterUserId}). Fetching household once.`);
        const household = await fetchHousehold(requesterUserId);
          setRequesterHousehold(household);
          setResponderHousehold(household);
      } else {
        console.log('Requester and Responder are different. Fetching households separately.');
        if (requesterUserId) {
          console.log(`Fetching requester household for user_id: ${requesterUserId}`);
          const requester = await fetchHousehold(requesterUserId);
          setRequesterHousehold(requester);
        } else {
          setRequesterHousehold(null);
        }
        
        if (responderUserId) {
          console.log(`Fetching responder household for user_id: ${responderUserId}`);
          const responder = await fetchHousehold(responderUserId);
          setResponderHousehold(responder);
        } else {
          setResponderHousehold(null);
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(`Failed to load data: ${err.message}`);
      setRequesterHousehold(null);
      setResponderHousehold(null);
    } finally {
      setLoading(false);
    }
  }

  async function fetchHousehold(userId) {
    if (!userId) return null;
    console.log(`Fetching household for userId: ${userId}`);
    
    const { data, error } = await supabase
      .from('households')
      .select('id, user_id, members, created_at, updated_at')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error(`Error fetching household for user ${userId}:`, error);
      return null;
    }

    console.log(`Household data for user ${userId}:`, data);

    if (data) {
      if (typeof data.members === 'string') {
        try {
          data.members = JSON.parse(data.members);
        } catch (e) {
          console.error('Error parsing members JSON:', e);
          data.members = [];
        }
      }
      if (!Array.isArray(data.members)) {
        data.members = [];
      }
      return data;
    }
    
    return null;
  }

  function openEditHousehold(household) {
    if (!household) {
      setError("No household data to edit.");
      return;
    }
    setEditHousehold(household);
    setEditMembers(household && Array.isArray(household.members) ? [...household.members] : []);
    setEditModalOpen(true);
  }

  function handleEditMemberChange(index, field, value) {
    setEditMembers(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }

  function addEditMember() {
    setEditMembers(prev => [...prev, { name: '', age: '', gender: '' }]);
  }

  function removeEditMember(index) {
    setEditMembers(prev => prev.filter((_, i) => i !== index));
  }

  async function saveEditHousehold() {
    setEditLoading(true);
    setError('');
    try {
      const { error } = await supabase
        .from('households')
        .update({ members: editMembers })
        .eq('id', editHousehold.id);
      if (error) throw error;
      // Audit log for edit household
      if (currentUser) {
        await supabase.from('audit_logs').insert([{
          user_id: currentUser.id,
          action: 'edit household',
          target_id: editHousehold.id,
          target_type: 'TemporaryMonitoring',
          details: { members: editMembers }
        }]);
      }
      setEditModalOpen(false);
      setEditHousehold(null);
      setEditMembers([]);
      // Refresh households after save
      await fetchShelterAndHouseholds();
    } catch (err) {
      setError('Failed to save household: ' + (err.message || 'Unknown error'));
    } finally {
      setEditLoading(false);
    }
  }

  async function fetchShelterHistory() {
    if (!shelterId) return;
    
    setHistoryLoading(true);
    try {
      // Get all SOS beacons where this shelter's provider was the responder
      const { data: shelterData, error: shelterError } = await supabase
        .from('temp_shelter')
        .select('shelter_provider')
        .eq('id', shelterId)
        .single();

      if (shelterError || !shelterData?.shelter_provider) {
        console.log('No shelter provider found for history');
        setShelterHistory([]);
        return;
      }

      // Get the profile ID of the shelter provider
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('user_id')
        .eq('id', shelterData.shelter_provider)
        .single();

      if (profileError || !profile?.user_id) {
        console.log('No profile found for shelter provider');
        setShelterHistory([]);
        return;
      }

      // Get all SOS beacons where this shelter provider was the responder
      const { data: beacons, error: beaconsError } = await supabase
        .from('sos_beacons')
        .select('*')
        .eq('responder_id', profile.user_id)
        .order('created_at', { ascending: false });

      if (beaconsError) {
        console.error('Error fetching beacons for history:', beaconsError);
        setShelterHistory([]);
        return;
      }

      // For each beacon, get the requester's household information
      const historyData = [];
      for (const beacon of beacons) {
        if (beacon.user_id) {
          const { data: household, error: householdError } = await supabase
            .from('households')
            .select('*')
            .eq('user_id', beacon.user_id)
            .maybeSingle();

          if (!householdError && household) {
            // Parse members if it's a string
            if (typeof household.members === 'string') {
              try {
                household.members = JSON.parse(household.members);
              } catch (e) {
                household.members = [];
              }
            }
            if (!Array.isArray(household.members)) {
              household.members = [];
            }

            // Get requester profile information
            const { data: requesterProfile, error: profileError } = await supabase
              .from('profiles')
              .select('first_name, last_name')
              .eq('user_id', beacon.user_id)
              .single();

            historyData.push({
              beacon_id: beacon.id,
              requester_name: profileError ? 'Unknown' : `${requesterProfile.first_name || ''} ${requesterProfile.last_name || ''}`.trim(),
              household: household,
              accepted_date: beacon.created_at,
              status: beacon.status,
              location: {
                latitude: beacon.latitude,
                longitude: beacon.longitude
              }
            });
          }
        }
      }

      setShelterHistory(historyData);
    } catch (err) {
      console.error('Error fetching shelter history:', err);
      setShelterHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }

  async function fetchCurrentUser() {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (profileError) {
        console.error('Error fetching current user profile:', profileError);
        setCurrentUser(null);
      } else {
        setCurrentUser(profile);
      }
    } else {
      setCurrentUser(null);
    }
  }

  if (loading) return <Preloader text="Loading Shelter Data..." />;
  if (error) return <div style={{ color: 'red', padding: 32 }}>{error}</div>;

  console.log('Rendering component with households:', { requesterHousehold, responderHousehold });

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
      <Sidebar onNarrowChange={setSidebarNarrow}/>
      <div style={{ flex: 1, padding: 32, width: '100%', overflowX: 'auto' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Mobile Hamburger Menu */}
            <MobileHamburger
              isOpen={showMobileMenu}
              onToggle={() => setShowMobileMenu(prev => !prev)}
            />
          <button 
            onClick={() => navigate('/temporary-shelters')}
            style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', marginRight: 10, color: '#333' }}
          >
            <FaArrowLeft />
          </button>
          <div className="title-text">Temporary Shelter Monitoring</div>
          </div>
          <ProfileMenu />
        </div>

        {/* Mobile Dropdown Menu */}
        <MobileMenu 
          isOpen={showMobileMenu} 
          onClose={() => setShowMobileMenu(false)} 
        />

        {currentUser?.role === 'barangay' && (
          <div style={{
            background: '#fff3cd',
            color: '#856404',
            border: '1px solid #ffeeba',
            borderRadius: 8,
            padding: '16px 24px',
            marginBottom: 24,
            fontWeight: 500,
            fontSize: 16
          }}>
            Barangay users have view-only access to shelter monitoring. Please contact an admin for changes.
          </div>
        )}
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px #0001' }}>
          <h2 style={{ fontSize: 20, marginBottom: 12 }}>Household Information</h2>
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap',
            gap: 32,
            alignItems: 'flex-start'
          }}>
            {/* Requester Household */}
            <div style={{ 
              flex: '1 1 400px',
              minWidth: '300px',
              background: '#f8f9fa', 
              borderRadius: 8, 
              padding: 16, 
              border: '1px solid #eee' 
            }}>
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap',
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: 8,
                gap: 8
              }}>
                <h3 style={{ fontSize: 17, margin: 0 }}>Requester Family</h3>
                {currentUser?.role !== 'barangay' && (
                <button 
                  onClick={() => openEditHousehold(requesterHousehold)} 
                  disabled={!requesterHousehold}
                  style={{ 
                    background: '#19c37d', 
                    color: '#fff', 
                    border: 'none', 
                    borderRadius: 6, 
                    padding: '6px 16px', 
                    fontWeight: 'bold', 
                    cursor: 'pointer', 
                    opacity: !requesterHousehold ? 0.5 : 1,
                    whiteSpace: 'nowrap',
                    minWidth: 'fit-content'
                  }}
                >
                  Edit Household
                </button>
                )}
              </div>
              {requesterHousehold ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '300px' }}>
                    <thead>
                      <tr style={{ background: '#e6f4ea' }}>
                      <th style={{ padding: 10, borderBottom: '1px solid #eaf6fb', whiteSpace: 'nowrap' }}>Name</th>
                      <th style={{ padding: 10, borderBottom: '1px solid #eaf6fb', textAlign: 'center', whiteSpace: 'nowrap' }}>Age</th>
                      <th style={{ padding: 10, borderBottom: '1px solid #eaf6fb', textAlign: 'center', whiteSpace: 'nowrap' }}>Gender</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(requesterHousehold.members) && requesterHousehold.members.length > 0 ? (
                      requesterHousehold.members.map((member, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: 10 }}>{member.name}</td>
                          <td style={{ padding: 10, textAlign: 'center' }}>{member.age}</td>
                          <td style={{ padding: 10, textAlign: 'center' }}>{member.gender}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan={3} style={{ textAlign: 'center', color: '#888' }}>No members found</td></tr>
                    )}
                  </tbody>
                </table>
                </div>
              ) : (
                <div style={{ color: '#888' }}>No household data found.</div>
              )}
            </div>
            {/* Responder Household */}
            <div style={{ 
              flex: '1 1 400px',
              minWidth: '300px',
              background: '#f8f9fa', 
              borderRadius: 8, 
              padding: 16, 
              border: '1px solid #eee' 
            }}>
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap',
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: 8,
                gap: 8
              }}>
                <h3 style={{ fontSize: 17, margin: 0 }}>Responder Family</h3>
                {currentUser?.role !== 'barangay' && (
                <button 
                  onClick={() => openEditHousehold(responderHousehold)} 
                  disabled={!responderHousehold}
                  style={{ 
                    background: '#19c37d', 
                    color: '#fff', 
                    border: 'none', 
                    borderRadius: 6, 
                    padding: '6px 16px', 
                    fontWeight: 'bold', 
                    cursor: 'pointer', 
                    opacity: !responderHousehold ? 0.5 : 1,
                    whiteSpace: 'nowrap',
                    minWidth: 'fit-content'
                  }}
                >
                  Edit Household
                </button>
                )}
              </div>
              {responderHousehold ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '300px' }}>
                    <thead>
                      <tr style={{ background: '#ffe6e6' }}>
                      <th style={{ padding: 10, borderBottom: '1px solid #ffe6e6', whiteSpace: 'nowrap' }}>Name</th>
                      <th style={{ padding: 10, borderBottom: '1px solid #ffe6e6', textAlign: 'center', whiteSpace: 'nowrap' }}>Age</th>
                      <th style={{ padding: 10, borderBottom: '1px solid #ffe6e6', textAlign: 'center', whiteSpace: 'nowrap' }}>Gender</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(responderHousehold.members) && responderHousehold.members.length > 0 ? (
                      responderHousehold.members.map((member, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: 10 }}>{member.name}</td>
                          <td style={{ padding: 10, textAlign: 'center' }}>{member.age}</td>
                          <td style={{ padding: 10, textAlign: 'center' }}>{member.gender}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan={3} style={{ textAlign: 'center', color: '#888' }}>No members found</td></tr>
                    )}
                  </tbody>
                </table>
                </div>
              ) : (
                <div style={{ color: '#888' }}>No household data found.</div>
              )}
            </div>
          </div>
        </div>
        {/* History Section */}
        {currentUser?.role !== 'barangay' && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px #0001', marginTop: 24 }}>
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap',
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: 16,
            gap: 16
          }}>
            <h2 style={{ fontSize: 20, margin: 0 }}>Shelter History</h2>
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap',
              gap: 8,
              alignItems: 'center'
            }}>
              {showHistory && (
                <button 
                  onClick={fetchShelterHistory}
                  disabled={historyLoading}
                  style={{ 
                    background: '#007bff', 
                    color: '#fff', 
                    border: 'none', 
                    borderRadius: 6, 
                    padding: '8px 16px', 
                    fontWeight: 'bold', 
                    cursor: historyLoading ? 'not-allowed' : 'pointer',
                    opacity: historyLoading ? 0.7 : 1,
                    whiteSpace: 'nowrap',
                    minWidth: 'fit-content'
                  }}
                >
                  {historyLoading ? 'Refreshing...' : 'Refresh'}
                </button>
              )}
              <button 
                onClick={() => setShowHistory(!showHistory)}
                style={{ 
                  background: showHistory ? '#dc3545' : '#19c37d', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: 6, 
                  padding: '8px 16px', 
                  fontWeight: 'bold', 
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  minWidth: 'fit-content'
                }}
              >
                {showHistory ? 'Hide History' : 'Show History'}
              </button>
            </div>
          </div>
          
          {showHistory && (
            <div>
              {historyLoading ? (
                <div style={{ textAlign: 'center', padding: 20, color: '#888' }}>Loading history...</div>
              ) : shelterHistory.length > 0 ? (
                <div>
                  {/* History Summary */}
                  <div style={{ 
                    background: '#e6f4ea', 
                    borderRadius: 8, 
                    padding: 16, 
                    marginBottom: 20,
                    border: '1px solid #19c37d'
                  }}>
                    <h3 style={{ fontSize: 16, margin: '0 0 12px 0', color: '#19c37d' }}>
                      History Summary {historyFilter !== 'all' && `(${historyFilter})`}
                    </h3>
                    <div style={{ 
                      display: 'flex', 
                      gap: 24, 
                      flexWrap: 'wrap',
                      alignItems: 'center'
                    }}>
                      <div style={{ whiteSpace: 'nowrap' }}>
                        <strong>Total Households Accepted:</strong> {shelterHistory.length}
                      </div>
                      <div style={{ whiteSpace: 'nowrap' }}>
                        <strong>Showing:</strong> {shelterHistory.filter(item => historyFilter === 'all' || item.status === historyFilter).length} {historyFilter !== 'all' && `(${historyFilter})`}
                      </div>
                      <div style={{ whiteSpace: 'nowrap' }}>
                        <strong>Date Range:</strong> {
                          shelterHistory.length > 0 ? 
                          `${new Date(shelterHistory[shelterHistory.length - 1].accepted_date).toLocaleDateString()} - ${new Date(shelterHistory[0].accepted_date).toLocaleDateString()}` : 
                          'N/A'
                        }
                      </div>
                      <div style={{ whiteSpace: 'nowrap' }}>
                        <strong>Active Cases:</strong> {shelterHistory.filter(item => item.status === 'active').length}
                      </div>
                      <div style={{ whiteSpace: 'nowrap' }}>
                        <strong>Resolved Cases:</strong> {shelterHistory.filter(item => item.status === 'resolved').length}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    {/* Filter Controls */}
                    <div style={{ 
                      marginBottom: 16, 
                      display: 'flex', 
                      flexWrap: 'wrap',
                      gap: 12, 
                      alignItems: 'center' 
                    }}>
                      <label style={{ 
                        fontSize: 14, 
                        fontWeight: 'bold', 
                        color: '#555',
                        whiteSpace: 'nowrap'
                      }}>Filter by Status:</label>
                      <select
                        value={historyFilter}
                        onChange={(e) => setHistoryFilter(e.target.value)}
                        style={{ 
                          padding: '6px 12px', 
                          borderRadius: 6, 
                          border: '1px solid #ccc',
                          fontSize: 14,
                          minWidth: '120px'
                        }}
                      >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="resolved">Resolved</option>
                        <option value="archived">Archived</option>
                      </select>
                    </div>
                    
                    {shelterHistory
                      .filter(item => historyFilter === 'all' || item.status === historyFilter)
                      .map((historyItem, index) => (
                        <div key={historyItem.beacon_id} style={{ 
                          border: '1px solid #eee', 
                          borderRadius: 8, 
                          padding: 16, 
                          marginBottom: 16,
                          background: '#f8f9fa',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          ':hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            transform: 'translateY(-2px)'
                          }
                        }}
                        onClick={() => navigate(`/sos-beacons/${historyItem.beacon_id}`)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = 'none';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                        >
                          <div style={{ 
                            display: 'flex', 
                            flexWrap: 'wrap',
                            justifyContent: 'space-between', 
                            alignItems: 'flex-start', 
                            marginBottom: 12,
                            gap: 12
                          }}>
                            <div style={{ flex: '1 1 300px', minWidth: '250px' }}>
                              <h3 style={{ fontSize: 16, margin: '0 0 8px 0', color: '#333' }}>
                                {historyItem.requester_name || 'Unknown Requester'}
                              </h3>
                              <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>
                                <strong>Accepted:</strong> {new Date(historyItem.accepted_date).toLocaleDateString()} at {new Date(historyItem.accepted_date).toLocaleTimeString()}
                              </div>
                              <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>
                                <strong>Status:</strong> 
                                <span style={{ 
                                  color: historyItem.status === 'resolved' ? '#19c37d' : 
                                         historyItem.status === 'active' ? '#ffc107' : '#dc3545',
                                  fontWeight: 'bold',
                                  marginLeft: 4
                                }}>
                                  {historyItem.status}
                                </span>
                              </div>
                              {historyItem.location.latitude && historyItem.location.longitude && (
                                <div style={{ fontSize: 14, color: '#666' }}>
                                  <strong>Location:</strong> {historyItem.location.latitude.toFixed(6)}, {historyItem.location.longitude.toFixed(6)}
                                </div>
                              )}
                            </div>
                            <div style={{ 
                              background: '#e6f4ea', 
                              color: '#19c37d', 
                              padding: '4px 8px', 
                              borderRadius: 4, 
                              fontSize: 12, 
                              fontWeight: 'bold',
                              whiteSpace: 'nowrap',
                              minWidth: 'fit-content'
                            }}>
                              Beacon #{historyItem.beacon_id}
                            </div>
                          </div>
                          
                          <div style={{ 
                            fontSize: 12, 
                            color: '#007bff', 
                            marginTop: 8, 
                            fontStyle: 'italic',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4
                          }}>
                            <span>Click to view SOS beacon details</span>
                            <span style={{ fontSize: 10 }}>→</span>
                          </div>
                          
                          {historyItem.household && Array.isArray(historyItem.household.members) && historyItem.household.members.length > 0 && (
                            <div>
                              <h4 style={{ fontSize: 14, margin: '12px 0 8px 0', color: '#555' }}>Household Members:</h4>
                              <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: '300px' }}>
                                  <thead>
                                    <tr style={{ background: '#e6f4ea' }}>
                                      <th style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: 'left', whiteSpace: 'nowrap' }}>Name</th>
                                      <th style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: 'left', whiteSpace: 'nowrap' }}>Age</th>
                                      <th style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: 'left', whiteSpace: 'nowrap' }}>Gender</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {historyItem.household.members.map((member, memberIdx) => (
                                      <tr key={memberIdx}>
                                        <td style={{ padding: '6px 8px', border: '1px solid #ddd' }}>{member.name}</td>
                                        <td style={{ padding: '6px 8px', border: '1px solid #ddd' }}>{member.age}</td>
                                        <td style={{ padding: '6px 8px', border: '1px solid #ddd' }}>{member.gender}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    
                    {shelterHistory.filter(item => historyFilter === 'all' || item.status === historyFilter).length === 0 && (
                      <div style={{ textAlign: 'center', padding: 20, color: '#888' }}>
                        No {historyFilter !== 'all' ? historyFilter : ''} cases found in the history.
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 20, color: '#888' }}>
                  No history found for this shelter. This shelter hasn't accepted any households yet.
                </div>
              )}
            </div>
          )}
        </div>
        )}
        {/* Edit Household Modal */}
        {editModalOpen && editHousehold && currentUser?.role !== 'barangay' && (
          <div style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 32, minWidth: 340, maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto' }}>
              <h2>Edit Household Members</h2>
              {editMembers.map((member, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                  <input
                    type="text"
                    placeholder="Name"
                    value={member.name}
                    onChange={e => handleEditMemberChange(idx, 'name', e.target.value)}
                    style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc', flex: 2 }}
                  />
                  <input
                    type="number"
                    placeholder="Age"
                    value={member.age}
                    onChange={e => handleEditMemberChange(idx, 'age', e.target.value)}
                    style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc', flex: 1 }}
                  />
                  <select
                    value={member.gender}
                    onChange={e => handleEditMemberChange(idx, 'gender', e.target.value)}
                    style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc', flex: 1 }}
                  >
                    <option value="">Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                  <button type="button" onClick={() => removeEditMember(idx)} style={{ background: '#ff4444', color: '#fff', border: 'none', borderRadius: 6, padding: '0 12px', fontWeight: 'bold', cursor: 'pointer' }}>Remove</button>
                </div>
              ))}
              <button type="button" onClick={addEditMember} style={{ background: '#19c37d', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 'bold', marginBottom: 16, cursor: 'pointer' }}>+ Add Member</button>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button type="button" onClick={() => setEditModalOpen(false)} style={{ background: '#eee', color: '#333', border: '1px solid #ccc', borderRadius: 8, padding: '10px 24px', cursor: 'pointer', fontSize: 16 }}>Cancel</button>
                <button type="button" onClick={saveEditHousehold} disabled={editLoading} style={{ background: '#19c37d', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 'bold', fontSize: 16, cursor: editLoading ? 'not-allowed' : 'pointer', opacity: editLoading ? 0.7 : 1 }}>{editLoading ? 'Saving...' : 'Save Changes'}</button>
              </div>
              {error && <div style={{ color: 'red', marginTop: 12 }}>{error}</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TempShelterMonitoring; 